/**
 * Narrow a course's subjects to the ones this tutor may host for the selected group.
 *
 * A group can be split between tutors by subject — one takes English and Verbal Reasoning, another
 * Maths and Non-Verbal Reasoning. `hostable_subject_ids` comes from `GET tutor/groups` and is:
 *
 *   null      the group is not split, so every subject of the course is hostable (the old behaviour)
 *   []        nothing is hostable here
 *   [ids]     exactly those subjects
 *
 * Whole-course classes (no group) are unaffected: pass a null group and the list comes back whole.
 *
 * This is a convenience for the picker only. CoursePlanStaffService::userCanHostForPlan() is the
 * authority and is checked again on the server when the class is created.
 */
import { useEffect, useMemo, useRef } from "react";

export function filterHostableSubjects(subjects, group) {
  const allowed = group?.hostable_subject_ids;
  if (!Array.isArray(allowed)) return subjects;
  const allowedKeys = allowed.map(String);
  return subjects.filter((s) => allowedKeys.includes(String(s.id)));
}

/**
 * The hostable list, plus a selection that cannot drift out of it.
 *
 * The pickers used to seed their selection from the *unfiltered* course subjects, so switching to a
 * group the tutor only covers Maths in could leave English selected — invisible in the dropdown
 * (MUI renders an out-of-range value as blank) and refused by the server on submit. This keeps the
 * two in step: a selection that is no longer hostable is cleared, and an empty one is filled in.
 *
 * @param autoSelect "single" fills in only when there is exactly one choice, so a tutor with a real
 *   choice makes it deliberately; "first" preselects the head of the list.
 */
export function useHostableSubjects(subjects, group, selectedId, onSelect, { autoSelect = "single" } = {}) {
  const hostable = useMemo(() => filterHostableSubjects(subjects, group), [subjects, group]);

  // The callers build this inline, so keeping it in a ref keeps the effect off the render loop.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const ids = hostable.map((s) => String(s.id));
    if (selectedId !== "" && selectedId != null && !ids.includes(String(selectedId))) {
      onSelectRef.current("");
      return;
    }
    if ((selectedId === "" || selectedId == null) && hostable.length > 0) {
      if (autoSelect === "first" || hostable.length === 1) {
        onSelectRef.current(hostable[0].id);
      }
    }
  }, [hostable, selectedId, autoSelect]);

  return hostable;
}

export default filterHostableSubjects;
